// TODO: split authData into user, project, session (or whatever makes sense)

import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { vueAuthData } from '@/api/vue';
import { useAppAlerts } from '@/stores/app/alerts';
import { TYPE_TAPWAGE } from '@/helpers/constants/integrationTypes';
import { DEMOGRAPHIC_SURVEY_SHOW, DEMOGRAPHIC_SURVEY_RESTRICTED } from '@/helpers/constants/demographicSurveyVersions';

export const useAppAuthData = defineStore('appAuthData', () => {
  const appAlerts = useAppAlerts();

  const authData = ref({
    appMaintenanceMode: false,
    atsName: '',
    atsId: '',
    integrationTypeKey: 0,
    integrationType: '',
    isSuperuser: false,
    isLoggedIn: false,
    userType: null,
    userSetupStatus: null,
    companyName: null,
    projectName: null,
    projectId: null,
    projectStatus: null,
    projectSetupStatus: null,
    projectMaintenanceMode: false,
    projectImageUrl: null,
    extensionJobsEnabled: false,
    showIntegrationsPage: false,
    isTrackingEnabled: false,
    loginMethod: '',
    oauthClient: '',
    ssoName: '',
    canDemographicSurveyReport: false,
    demographicSurveyReportVersion: 0,
    aiEnabled: false,
    noEditorEnabled: false,
    isRoleManagementRestricted: false,
    jobProfileEnabled: false,
    flsaEnabled: false,
    acknowledgementsEnabled: false,
    nextUiEnabled: false,
    ascentEnabled: false,
    inclusionMeterDisabled: false,
    accountExecutiveEmail: '',
    accountExecutiveName: '',
    userId: null,
    showWeeklyReports: false,
    isApplicationReportingEnabled: false,
    firstName: '',
    lastName: '',
    role: '',
    email: '',
    appVersion: '',
    personaKey: null,
    hasInstalledDA: false,
    ipAddress: null,
    jobProfileLevels: [],
    rbac: {
      roleNames: [],
      permissions: {},
    },
  });

  const integrated = computed(() => authData.value.integrationTypeKey !== TYPE_TAPWAGE);
  const integrationName = computed(() => (integrated.value ? authData.value.integrationType : ''));
  const isDatapeopleEmployee = computed(() => [
    'datapeople.io',
    'datapeople.fun',
    'taprecruit.me',
    'taprecruit.co',
    'tapwage.com',
    'payscale.com',
  ].some((domain) => authData.value.email?.endsWith(domain)) || authData.value.isSuperuser);
  const isDemographicSurveyReportVisible = computed(() => [
    DEMOGRAPHIC_SURVEY_SHOW,
    DEMOGRAPHIC_SURVEY_RESTRICTED,
  ].includes(authData.value.demographicSurveyReportVersion));
  const isDemographicSurveyReportRestricted = computed(
    () => authData.value.demographicSurveyReportVersion === DEMOGRAPHIC_SURVEY_RESTRICTED,
  );
  const demographicSurveyReportLabel = computed(() => {
    if (authData.value.demographicSurveyReportVersion === DEMOGRAPHIC_SURVEY_SHOW) {
      return 'Show';
    }
    if (authData.value.demographicSurveyReportVersion === DEMOGRAPHIC_SURVEY_RESTRICTED) {
      return 'Restricted';
    }
    return 'Hide';
  });
  const isAdmin = computed(() => authData.value.role === 'admin');

  const setAuthData = (data) => {
    const validData = {};

    // Only write data that we are expecting
    Object.keys(data).forEach((key) => {
      if (authData.value[key] !== undefined) {
        validData[key] = data[key];
      }
    });

    authData.value = validData;
  };

  const updatePersona = (personaKey) => {
    authData.value.personaKey = personaKey;
  };

  const addFlashMessages = (flashMessages) => {
    Object.keys(flashMessages || {}).forEach((type) => {
      flashMessages[type].forEach((message) => {
        appAlerts.addAlert({ type, message });
      });
    });
  };

  const fetchAuthData = async () => {
    try {
      const { flashMessages, ...responseJSON } = await vueAuthData();
      setAuthData(responseJSON);
      addFlashMessages(flashMessages);
    } catch (e) {
      // TODO: show proper error message
      // app initialization failed, `alert` is the only way to report the error
      if (
        e.message.endsWith('not found in appAuthData state')
        && ['app.datapeople.io', 'sandbox.datapeople.io'].includes(window.location.origin)
      ) {
        // eslint-disable-next-line no-console
        console.error(e.message);
        return;
      }

      window.alert(`AuthData: ${e}`);
      throw e;
    }
  };

  const toggleNextUi = () => {
    authData.value.nextUiEnabled = !authData.value.nextUiEnabled;
  };

  const setAiEnabled = (enabled) => {
    authData.value.aiEnabled = !!enabled;
  };

  const checkRbacPermissions = (permissions) => permissions.every(
    (permission) => Boolean(authData.value.rbac?.permissions?.[permission]),
  );

  const checkOneOfPermissions = (permissions = []) => permissions
    .some((permission) => authData.value.rbac?.permissions?.[permission]);

  return {
    authData,
    integrated,
    integrationName,
    isAdmin,
    fetchAuthData,
    setAuthData,
    toggleNextUi,
    setAiEnabled,
    updatePersona,
    checkRbacPermissions,
    checkOneOfPermissions,
    isDatapeopleEmployee,
    isDemographicSurveyReportVisible,
    isDemographicSurveyReportRestricted,
    demographicSurveyReportLabel,
  };
});
